import React, { Component } from 'react'
import axios from 'axios'
import { connect } from 'react-redux'

import sortBy from 'lodash/sortBy'

import PacePV from './PacePV'
import AuthA from '../../store/actions/AuthA'
import QuarterA from '../../store/actions/QuarterA'
import { resolveGradingScheme } from '../../utils/gradingScheme'
import { canEditMarks } from '../../utils/workflowStatus'
import { ratingKey, toBulkPayload } from './paceGrid'

const _ = { sortBy }

/**
 * Per-competency PACE entry for one student, one subject, one term.
 *
 * The form is filled progressively through the term rather than in one sitting,
 * so this saves whatever is currently set and leaves the rest blank. Ratings go
 * out in a single bulk request: Reading and Literacy alone is around forty
 * competencies across up to four macro skills, which the one-request-per-value
 * pattern used elsewhere would turn into ~160 round trips per student.
 */
class PacePC extends Component {
  state = {
    section: null,
    sectionSubject: null,
    schoolYear: null,
    sectionStudents: [],
    sectionStudentId: '',
    pendingStudentId: null,
    competencies: [],
    ratings: {},      // `${competencyId}|${macroSkill}` -> 'A'..'E'
    loading: true,
    saving: false,
    dirty: false,
    error: null,
    success: false,
  }

  componentDidMount() {
    // Landing here directly - a link from the approval panel, or the card -
    // is a full page load with an empty store, and PacePV redirects to the
    // landing page unless the token is put back first.
    this.props.authFn.getToken()
    this.props.onContentMount({ title: 'PACE Form' })
    this.applyQuarterFromUrl()
    this.load()
  }

  componentDidUpdate(prevProps) {
    const changed =
      prevProps.match.params.sectionSubjectId !== this.props.match.params.sectionSubjectId ||
      prevProps.currentQuarter !== this.props.currentQuarter

    if (prevProps.location.search !== this.props.location.search) {
      this.applyQuarterFromUrl()
    }
    if (changed) this.load()
  }

  applyQuarterFromUrl() {
    const params = new URLSearchParams(this.props.location.search)
    const quarter = parseInt(params.get('quarter'), 10)
    if (quarter >= 1 && quarter <= 4 && quarter !== this.props.currentQuarter) {
      this.props.quarterActions.setCurrentQuarter(quarter)
    }
  }

  get scheme() {
    const { section, schoolYear } = this.state
    if (!section || !schoolYear) return null
    return resolveGradingScheme(schoolYear, section.gradeLevel, {
      levelType: section.levelType,
    })
  }

  /**
   * The workflow row that governs this grid. PACE ratings have no ladder of
   * their own: they ride on the grade submission for this section-subject and
   * term, so that the summary letter and the ratings beneath it cannot end up
   * on opposite sides of an approval.
   */
  get sectionSubjectDetail() {
    const { sectionSubject } = this.state
    if (!sectionSubject || !sectionSubject.details) return null
    return sectionSubject.details.find((d) =>
      d.type === 'grade' && d.quarter === this.term && d.active !== false) || null
  }

  get canEdit() {
    const detail = this.sectionSubjectDetail
    if (!detail) return false
    return canEditMarks(detail.status, {
      userType: this.props.userType,
      isSubjectTeacher: this.state.sectionSubject.teacherId === this.props.userId,
    })
  }

  get term() {
    const term = parseInt(this.props.currentQuarter, 10) || 1
    // Redux can still be holding a period from a four-quarter year. Without
    // this the grid renders every row as "not assessed this term" and says
    // nothing about why.
    const scheme = this.scheme
    if (scheme && !scheme.isValidPeriod(term)) return scheme.periods[0].index
    return term
  }

  api(path) {
    return process.env.REACT_APP_URL + path
  }

  load() {
    const { sectionId, sectionSubjectId } = this.props.match.params
    this.setState({ loading: true, error: null })

    const sectionSubjectUrl = this.api(
      '/sectionSubjects?filter[where][sectionSubjectId]=' + sectionSubjectId +
      '&filter[include]=subject&filter[include]=section&filter[include]=details')

    axios.get(sectionSubjectUrl)
      .then((response) => {
        const sectionSubject = response.data[0]
        if (!sectionSubject) {
          return this.setState({ loading: false, error: 'This subject could not be found.' })
        }

        const section = sectionSubject.section
        return axios
          .get(this.api('/schoolYears/' + section.schoolYearId))
          .then((syResponse) => {
            this.setState(
              { sectionSubject, section, schoolYear: syResponse.data },
              () => {
                this.loadCompetencies(sectionSubject.subject.subjectCategory, section.gradeLevel)
                this.loadStudents(sectionId)
              }
            )
          })
      })
      .catch(() => this.setState({
        loading: false,
        error: 'Could not load this subject. Please refresh and try again.',
      }))
  }

  loadCompetencies(subjectCategory, gradeLevel) {
    const url = this.api(
      '/competencies?filter[where][active]=true' +
      '&filter[where][subjectKey]=' + encodeURIComponent(subjectCategory) +
      '&filter[where][gradeLevel]=' + encodeURIComponent(gradeLevel) +
      '&filter[order]=sortOrder ASC')

    axios.get(url)
      .then((response) => this.setState({ competencies: response.data, loading: false }))
      .catch(() => this.setState({
        loading: false,
        error: 'Could not load the competency list. It may not have been seeded for this subject yet.',
      }))
  }

  loadStudents(sectionId) {
    const url = this.api(
      '/sectionStudents?filter[where][active]=true&filter[include]=student' +
      '&filter[where][sectionId]=' + sectionId)

    axios.get(url).then((response) => {
      const sectionStudents = _.sortBy(response.data, (s) => s.student.lastName)
      const first = sectionStudents[0]
      this.setState(
        { sectionStudents, sectionStudentId: first ? first.sectionStudentId : '' },
        () => { if (first) this.loadRatings(first.sectionStudentId) }
      )
    })
  }

  loadRatings(sectionStudentId) {
    // Scoped to this subject as well as this learner. Without it the grid loads
    // every subject's ratings for the term, and handleSave posts the lot back
    // under this subject's id - silently re-parenting another subject's work.
    const { sectionSubjectId } = this.props.match.params
    const url = this.api(
      '/competencyRatings?filter[where][active]=true' +
      '&filter[where][sectionStudentId]=' + sectionStudentId +
      '&filter[where][sectionSubjectId]=' + sectionSubjectId +
      '&filter[where][term]=' + this.term)

    axios.get(url).then((response) => {
      const ratings = {}
      response.data.forEach((r) => {
        ratings[ratingKey(r.competencyId, r.macroSkill)] = r.rating
      })
      this.setState({ ratings, dirty: false, success: false })
    })
  }

  handleStudentChange = (e, { value }) => {
    // Ratings are held locally until Save, so switching learner would drop
    // however many the teacher had just entered, with no sign it had happened.
    if (this.state.dirty) {
      this.setState({ pendingStudentId: value })
      return
    }
    this.switchStudent(value)
  }

  switchStudent = (value) => {
    this.setState({ sectionStudentId: value, ratings: {}, pendingStudentId: null },
      () => this.loadRatings(value))
  }

  handleDiscardAndSwitch = () => this.switchStudent(this.state.pendingStudentId)

  handleCancelSwitch = () => this.setState({ pendingStudentId: null })

  handleRatingChange = (competencyId, macroSkill, rating) => {
    const key = ratingKey(competencyId, macroSkill)
    this.setState((prev) => ({
      ratings: Object.assign({}, prev.ratings, { [key]: rating }),
      dirty: true,
      success: false,
    }))
  }

  handleSave = () => {
    const { sectionStudentId, sectionSubject, ratings } = this.state
    if (!sectionStudentId || !sectionSubject) return

    // Only what is actually set. A blank cell is a competency the teacher has
    // not reached yet, not a rating of nothing.
    const payload = toBulkPayload(ratings)

    if (payload.length === 0) {
      return this.setState({ error: 'Nothing to save yet.', success: false })
    }

    this.setState({ saving: true, error: null })

    axios.post(this.api('/competencyRatings/bulk'), {
      sectionStudentId,
      sectionSubjectId: sectionSubject.sectionSubjectId,
      term: this.term,
      ratings: payload,
      ratedBy: this.props.username,
    })
      .then(() => this.setState({ saving: false, dirty: false, success: true, error: null }))
      .catch((err) => {
        const message = err.response && err.response.data && err.response.data.error
          ? err.response.data.error.message
          : 'Could not save. Please try again.'
        this.setState({ saving: false, error: message, success: false })
      })
  }

  render() {
    return (
      <PacePV
        {...this.state}
        auth={this.props.auth}
        userType={this.props.userType}
        scheme={this.scheme}
        term={this.term}
        sectionSubjectDetail={this.sectionSubjectDetail}
        canEdit={this.canEdit}
        onStudentChange={this.handleStudentChange}
        onDiscardAndSwitch={this.handleDiscardAndSwitch}
        onCancelSwitch={this.handleCancelSwitch}
        onRatingChange={this.handleRatingChange}
        onSave={this.handleSave}
      />
    )
  }
}

const mapDispatchToProps = (dispatch) => ({
  authFn: AuthA(dispatch),
  quarterActions: QuarterA(dispatch),
})

const mapStateToProps = (state) => ({
  auth: state.AuthR.auth,
  username: state.AuthR.username,
  userType: state.AuthR.userType,
  userId: state.AuthR.userId,
  currentQuarter: state.QuarterR.currentQuarter,
})

export default connect(mapStateToProps, mapDispatchToProps)(PacePC)
